import type {
  AdminInstructorListRecord,
  AdUser,
  Role,
  StudentListRecord,
  SvelteFetch,
  User,
  UserClassSummary,
} from '$lib/api/types';
import { fetchJson, getJson } from '$lib/api/fetch';

export async function listAdmins(
  search: string,
  page: number,
  pageSize: number = 20,
  signal: AbortSignal,
): Promise<AdminInstructorListRecord[]> {
  return await getJson(`/users/admins?${search}&page=${page}&pageSize=${pageSize}`, signal);
}

export async function listInstructors(
  search: string,
  page: number,
  pageSize: number = 20,
  signal: AbortSignal,
): Promise<AdminInstructorListRecord[]> {
  return await getJson(`/users/instructors?${search}&page=${page}&pageSize=${pageSize}`, signal);
}

export async function listStudents(
  search: string,
  page: number,
  pageSize: number = 20,
  signal: AbortSignal,
): Promise<StudentListRecord[]> {
  return await getJson(`/users/students?${search}&page=${page}&pageSize=${pageSize}`, signal);
}

export async function listStudentsForAddingToClass(
  classId: number,
  search: string,
  page: number,
  pageSize: number = 20,
  signal: AbortSignal,
): Promise<Pick<User, 'id' | 'lastName' | 'firstName'>[]> {
  return await getJson(
    `/users/AddStudentToClassQuickSearch?classId=${classId}&search=${search}&page=${page}&pageSize=${pageSize}`,
    signal,
  );
}

export async function userAutocomplete(
  search: string,
  roles: Role,
  page: number,
  pageSize = 10,
): Promise<Pick<User, 'id' | 'firstName' | 'lastName' | 'role'>[]> {
  return await getJson(`/users/autocomplete?search=${search}&roles=${roles}&page=${page}&pageSize=${pageSize}`);
}

export async function adSearch(
  search: string,
  studentsOnly: boolean,
  page: number,
  pageSize = 10,
  signal: AbortSignal,
): Promise<AdUser[]> {
  return await getJson(
    `/users/adsearch?search=${search}&studentsOnly=${studentsOnly}&page=${page}&pageSize=${pageSize}`,
    signal,
  );
}

export async function addUser(universalId: string, role: Role): Promise<User> {
  return await fetchJson(`/users`, 'POST', {
    universalId,
    role,
  });
}

export async function getUser(id: number, fetch: SvelteFetch): Promise<User> {
  return await getJson(`/users/${id}`, undefined, fetch);
}

export async function getUserClasses(id: number, isStudent: boolean, fetch: SvelteFetch): Promise<UserClassSummary[]> {
  if (isStudent) {
    return await getJson(`/users/${id}/participatingclasses`, undefined, fetch);
  } else {
    return await getJson(`/users/${id}/instructingclasses`, undefined, fetch);
  }
}

export async function deactivateUser(id: number): Promise<User> {
  return await getJson(`/users/${id}/deactivate`);
}

export async function activateUser(id: number): Promise<User> {
  return await getJson(`/users/${id}/activate`);
}

export async function updateRole(id: number, role: Role): Promise<User> {
  return await getJson(`/users/${id}/updaterole?role=${role}`);
}

export async function startImpersonation(userId: number): Promise<void> {
  await fetchJson('/auth/impersonate', 'POST', { userId }, true);
}

export async function stopImpersonation(): Promise<void> {
  await fetchJson('/auth/impersonate/stop', 'GET', undefined, true);
}
